import { formatBytes } from '@dbrain/app-core/format';
import { timeSuffix } from '@dbrain/app-core/turkish';
import type { ErrorCode, SkillSourceFailureDto } from '@dbrain/shared';

/**
 * Phase 16c.3 (SkillGitHubHatalar.dc.html, SkillGitHubCokBuyuk.dc.html): why a
 * skill could not be installed, as the card in "Skill ekle" says it — what
 * happened, what to do, the fitting button, and "Teknik ayrıntılar" (step,
 * address, answer, time, error number). GitHub's words for a GitHub source,
 * plainer ones for a ZIP or SKILL.md link. DOM-free, so it is tested alone.
 */
export const CARD_CODES: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'skill_too_large',
  'skill_source_rate_limited',
  'skill_source_not_allowed',
  'skill_source_not_found',
  'skill_source_timeout',
  'skill_source_network',
  'skill_no_skill_md',
  'skill_source_unavailable',
]);

type T = (key: string, options?: Record<string, unknown>) => string;

export interface CardText {
  key: string;
  values?: Record<string, unknown>;
}

export type CardAction = { kind: 'upload' | 'retry'; primary: boolean } | { kind: 'wait'; minutes: number };

export interface CardRow {
  key: 'step' | 'address' | 'answer' | 'connection' | 'fallback' | 'hosts' | 'time' | 'error';
  label: string;
  value: string;
  mono?: boolean;
}

export interface SourceErrorView {
  title: CardText;
  body: CardText[];
  actions: CardAction[];
  rows: CardRow[];
}

const MB = 1024 * 1024;

/** "26 MB" in a sentence (whole megabytes from ten on), "26,3 MB" in the details */
export function sentenceSize(bytes: number, locale: string): string {
  if (bytes >= 10 * MB) return `${Math.round(bytes / MB).toLocaleString(locale)}\u00a0MB`;
  return formatBytes(bytes, locale);
}

export function clockOf(iso: string, locale: string, zone: string): string {
  return new Date(iso).toLocaleTimeString(locale.startsWith('en') ? 'en-GB' : locale, { hour: '2-digit', minute: '2-digit', timeZone: zone });
}

/** "3,8 sn"; under a tenth of a second "0,1 sn’den kısa" (16c.1's rule: never "0 sn") */
function seconds(ms: number, locale: string, t: T): string {
  if (ms < 100) return t('skills.sourceError.underTenth', { value: (0.1).toLocaleString(locale) });
  return t('skills.sourceError.seconds', { value: (ms / 1000).toLocaleString(locale, { maximumFractionDigits: 1 }) });
}

export function sourceErrorView(code: ErrorCode, detail: SkillSourceFailureDto, t: T, locale: string, zone: string, now = Date.now()): SourceErrorView {
  const github = detail.kind !== 'link';
  const words = github ? 'skills.sourceError.github' : 'skills.sourceError.link';
  const en = locale.startsWith('en');
  const size = (bytes: number | undefined) => formatBytes(bytes ?? 0, locale);
  const timeoutSeconds = Math.round((detail.timeoutMs ?? 60_000) / 1000);
  let title: CardText = { key: `${words}.unavailable.title` };
  let body: CardText[] = [];
  let actions: CardAction[] = [];
  let answer = detail.status !== undefined ? t('skills.sourceError.answers.status', { status: detail.status }) : t('skills.sourceError.answers.none');
  let hosts: string | null = null;

  switch (code) {
    case 'skill_too_large': {
      const reason = detail.overReason ?? 'bytes';
      title = { key: 'skills.sourceError.github.tooLarge.title' };
      if (reason === 'file' && detail.biggest) {
        body = [{ key: 'skills.sourceError.github.tooLarge.file', values: { path: detail.biggest.path, size: sentenceSize(detail.biggest.size, locale), limit: sentenceSize(detail.limitFileBytes ?? 10 * MB, locale) } }];
        answer = t('skills.sourceError.answers.tooLargeFile', { path: detail.biggest.path, size: size(detail.biggest.size), limit: size(detail.limitFileBytes) });
      } else {
        body = [
          reason === 'files'
            ? { key: 'skills.sourceError.github.tooLarge.files', values: { files: (detail.files ?? 0).toLocaleString(locale), limit: (detail.limitFiles ?? 500).toLocaleString(locale) } }
            : { key: 'skills.sourceError.github.tooLarge.bytes', values: { size: sentenceSize(detail.bytes ?? 0, locale), limit: sentenceSize(detail.limitBytes ?? 20 * MB, locale) } },
        ];
        answer = t('skills.sourceError.answers.tooLarge', { size: size(detail.bytes), files: (detail.files ?? 0).toLocaleString(locale), limit: sentenceSize(detail.limitBytes ?? 20 * MB, locale), limitFiles: (detail.limitFiles ?? 500).toLocaleString(locale) });
      }
      actions = [{ kind: 'upload', primary: true }];
      break;
    }
    case 'skill_source_rate_limited': {
      title = { key: 'skills.sourceError.github.rateLimited.title' };
      const reset = detail.resetAt ? Date.parse(detail.resetAt) : NaN;
      const clock = detail.resetAt ? clockOf(detail.resetAt, locale, zone) : '';
      if (!Number.isNaN(reset) && reset > now) {
        const minutes = Math.max(1, Math.ceil((reset - now) / 60_000));
        body = [{ key: 'skills.sourceError.github.rateLimited.when', values: { time: clock, from: en ? '' : timeSuffix(clock, 'ablative'), minutes } }];
        actions = [{ kind: 'wait', minutes }, { kind: 'upload', primary: false }];
      } else {
        body = [{ key: Number.isNaN(reset) ? 'skills.sourceError.github.rateLimited.unknown' : 'skills.sourceError.github.rateLimited.passed' }];
        actions = [{ kind: 'retry', primary: true }, { kind: 'upload', primary: false }];
      }
      body.push({ key: 'skills.sourceError.github.rateLimited.zip' });
      answer = detail.resetAt
        ? t('skills.sourceError.answers.rateLimited', { status: detail.status ?? 403, remaining: detail.remaining ?? '—', time: clock })
        : t('skills.sourceError.answers.rateLimitedNoTime', { status: detail.status ?? 403 });
      break;
    }
    case 'skill_source_not_allowed': {
      const deny = detail.policy === 'deny';
      title = { key: `${words}.notAllowed.title` };
      body = [
        { key: `${words}.notAllowed.${deny ? 'deny' : 'allow'}`, values: { host: detail.address } },
        { key: `skills.sourceError.github.notAllowed.${deny ? 'adminDeny' : 'admin'}` },
      ];
      actions = [{ kind: 'upload', primary: true }];
      answer = t('skills.sourceError.answers.policy');
      hosts = (detail.hosts ?? [detail.address]).join(', ');
      break;
    }
    case 'skill_source_not_found':
      title = { key: `${words}.notFound.title` };
      body = github ? [{ key: `${words}.notFound.body` }, { key: `${words}.notFound.zip` }] : [{ key: `${words}.notFound.body` }];
      actions = [{ kind: 'upload', primary: true }];
      break;
    case 'skill_source_timeout':
      title = { key: `${words}.timeout.title` };
      body = [{ key: `${words}.timeout.body`, values: { seconds: timeoutSeconds } }];
      actions = [{ kind: 'retry', primary: true }];
      answer = t('skills.sourceError.answers.timeout', { seconds: timeoutSeconds });
      break;
    case 'skill_source_network':
      title = { key: `${words}.network.title` };
      body = [{ key: `${words}.network.body` }, { key: 'skills.sourceError.github.network.admin' }];
      actions = [{ kind: 'retry', primary: true }];
      answer =
        detail.proxyStatus !== undefined
          ? t('skills.sourceError.answers.networkProxy', { status: detail.proxyStatus })
          : t('skills.sourceError.answers.network', { cause: detail.cause ?? (en ? 'no answer' : 'cevap yok') });
      break;
    case 'skill_no_skill_md':
      title = { key: `${words}.noSkill.title` };
      body = [{ key: `${words}.noSkill.body` }];
      if (detail.repoFiles !== undefined) answer = t(detail.truncated ? 'skills.sourceError.answers.noSkillCut' : 'skills.sourceError.answers.noSkill', { files: detail.repoFiles.toLocaleString(locale) });
      break;
    default:
      body = [{ key: `${words}.unavailable.body` }];
      actions = [{ kind: 'retry', primary: true }, { kind: 'upload', primary: false }];
  }

  const step = !github && detail.step === 'download' ? 'linkDownload' : detail.step;
  const rows: CardRow[] = [
    { key: 'step', label: t('skills.sourceError.labels.step'), value: t(`skills.sourceError.steps.${step}`) },
    { key: 'address', label: t('skills.sourceError.labels.address'), value: detail.address, mono: true },
    { key: 'answer', label: t('skills.sourceError.labels.answer'), value: answer },
  ];
  // Phase 16c.4: a connection that failed says at which step, and whether the classic TLS hello was tried (D-397)
  if (detail.fallback || detail.connectStage) {
    const stage = detail.connectStage ?? (detail.phase === 'body' ? 'body' : detail.cause === 'ENOTFOUND' || detail.cause === 'EAI_AGAIN' ? 'name_check' : 'unknown');
    rows.push({ key: 'connection', label: t('skills.sourceError.labels.connection'), value: t(`skills.sourceError.connectStages.${stage}`) });
    if (detail.fallback) rows.push({ key: 'fallback', label: t('skills.sourceError.labels.fallback'), value: t(`skills.sourceError.fallbacks.${detail.fallback}`) });
  }
  if (hosts) rows.push({ key: 'hosts', label: t('skills.sourceError.labels.hosts'), value: hosts, mono: true });
  // nothing was sent when the firm's sites refused: no time to show
  const took = code === 'skill_source_not_allowed' && detail.status === undefined ? null : seconds(detail.durationMs, locale, t);
  if (took) rows.push({ key: 'time', label: t('skills.sourceError.labels.time'), value: took });
  rows.push({ key: 'error', label: t('skills.sourceError.labels.error'), value: detail.errorId, mono: true });
  return { title, body, actions, rows };
}
